/**
 * The SSE connection behind the build view.
 *
 * Owns reconnection, `Last-Event-ID` tracking, and replay dedup. Reconnection
 * is deliberately quiet: a dropped stream during a multi-minute build is
 * normal, and an error banner for a two-second gap alarms the user about
 * something that is already fixing itself.
 */

import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import type {
  Activity,
  BuildPhase,
  Framework,
  SessionSnapshot,
  TerminalSessionState,
} from "@hatch/shared";
import {
  DEFAULT_DEEP_THINK,
  DEFAULT_FRAMEWORK,
  PHASE_ORDER,
  SSE_ACTIVITY_EVENT,
} from "@hatch/shared";
import { buildViewModel } from "./viewModel.js";

export interface StreamState {
  activities: Activity[];
  phase: BuildPhase;
  outcome: TerminalSessionState | null;
  siteUrl: string | null;
  /**
   * The ephemeral Xano tenant behind `siteUrl`, when the deploy named one.
   *
   * Only ever arrives on a snapshot — the terminal activity carries the site
   * URL and nothing else — which is why the terminal handler below refetches
   * one. See `useSessionStream`.
   */
  xanoTenant: string | null;
  /**
   * What the build shipped, for the done screen to show rather than assert.
   *
   * Arrives with the tenant, on a snapshot, and each field is independently
   * absent when Hatch could not establish it. Absent is not zero: a pill with
   * no number behind it does not render at all. See `SessionSnapshot`.
   */
  xanoObjects: NonNullable<NonNullable<SessionSnapshot["xanoBackend"]>["objects"]> | null;
  /** Documents the deploy published, when it reported a count. */
  sitePages: number | null;
  /**
   * The short code a visitor reads aloud at the booth.
   *
   * Null for every session that did not arrive by SMS, which is most of them.
   * Only the `xano` skin draws it — this lives here rather than in that skin
   * because it arrives on the snapshot like every other field, and a skin
   * reaching for the snapshot directly is the seam this hook exists to close.
   */
  boothCode: string | null;
  failureMessage: string | null;
  connection: "connecting" | "open" | "reconnecting" | "closed";
  lastEventId: number;
  /** The originating prompt, so the header can show what was asked for. */
  prompt: string | null;
  /** Epoch ms the session was created, for total elapsed time. */
  createdAt: number | null;
  /**
   * Which frontend this session built with.
   *
   * Always on the wire — a session that never stated one carries the default —
   * so this starts at the default rather than at null, and no consumer has to
   * handle "not known yet". The done screen names it beside the address, which
   * is the one place a visitor can check that they got what they picked.
   */
  framework: Framework;
}

type Action =
  | { type: "activity"; activity: Activity }
  | { type: "connection"; status: StreamState["connection"] }
  | { type: "snapshot"; snapshot: SessionSnapshot };

const INITIAL: StreamState = {
  activities: [],
  phase: "queued",
  outcome: null,
  siteUrl: null,
  xanoTenant: null,
  xanoObjects: null,
  sitePages: null,
  boothCode: null,
  failureMessage: null,
  connection: "connecting",
  lastEventId: 0,
  prompt: null,
  createdAt: null,
  framework: DEFAULT_FRAMEWORK,
};

function reduce(state: StreamState, action: Action): StreamState {
  switch (action.type) {
    case "connection":
      return { ...state, connection: action.status };

    case "snapshot":
      // Seeds terminal state for a session that finished before we connected,
      // so a late viewer sees the result rather than an empty timeline.
      return {
        ...state,
        // Subject to the same never-regress rule as a phase activity. The
        // snapshot fetch and the SSE replay are started together and race: the
        // snapshot is read once, so a slow one can land after replay has
        // already carried the phase forward and drag it back to whatever was
        // true when the request was issued. Assigned unconditionally, that put
        // a timeline into reverse — and in the hatch skin it un-cracked the egg
        // and ran the progress bar backwards, the one motion this UI promises
        // it can never make.
        phase:
          PHASE_ORDER[action.snapshot.phase] > PHASE_ORDER[state.phase]
            ? action.snapshot.phase
            : state.phase,
        siteUrl: action.snapshot.siteUrl ?? state.siteUrl,
        xanoTenant: action.snapshot.xanoBackend?.tenant ?? state.xanoTenant,
        // `??` for the same reason every field here uses it: the snapshot fetch
        // and the SSE replay race, and a stale snapshot that arrives after the
        // terminal refetch must not clear counts already in hand.
        xanoObjects: action.snapshot.xanoBackend?.objects ?? state.xanoObjects,
        sitePages: action.snapshot.sitePages ?? state.sitePages,
        boothCode: action.snapshot.boothCode ?? state.boothCode,
        failureMessage: action.snapshot.failureMessage ?? state.failureMessage,
        prompt: action.snapshot.prompt ?? state.prompt,
        createdAt: action.snapshot.createdAt ?? state.createdAt,
        framework: action.snapshot.framework ?? state.framework,
      };

    case "activity": {
      const { activity } = action;
      // Replay and the live subscription can overlap while connecting.
      if (activity.seq <= state.lastEventId) return state;

      const next: StreamState = {
        ...state,
        lastEventId: activity.seq,
        activities: [...state.activities, activity],
      };

      switch (activity.kind) {
        case "phase":
          // Never regress: a backwards-jumping timeline reads as failure even
          // when the build is fine.
          return PHASE_ORDER[activity.phase] > PHASE_ORDER[state.phase]
            ? { ...next, phase: activity.phase }
            : next;
        case "terminal":
          return {
            ...next,
            outcome: activity.outcome,
            siteUrl: activity.siteUrl ?? null,
            failureMessage: activity.message ?? null,
            phase: activity.outcome === "succeeded" ? "done" : state.phase,
            connection: "closed",
          };
        default:
          return next;
      }
    }
  }
}

export function useSessionStream(sessionId: string | null) {
  const [state, dispatch] = useReducer(reduce, INITIAL);
  const lastEventIdRef = useRef(0);
  const retryRef = useRef(0);

  lastEventIdRef.current = state.lastEventId;

  useEffect(() => {
    if (!sessionId) return;

    let cancelled = false;
    let source: EventSource | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    const seedFromSnapshot = () =>
      fetch(`/api/sessions/${sessionId}`, { credentials: "same-origin" })
        .then((r) => (r.ok ? r.json() : null))
        .then((snapshot: SessionSnapshot | null) => {
          if (snapshot && !cancelled) dispatch({ type: "snapshot", snapshot });
        })
        .catch(() => {});

    // Seed from the snapshot so a reload onto a finished build shows its result
    // immediately rather than after the replay finishes.
    void seedFromSnapshot();

    const connect = () => {
      if (cancelled) return;

      // EventSource sends Last-Event-ID automatically on ITS own reconnects,
      // but we manage reconnection ourselves to control backoff, so the
      // resume point travels as a query parameter too.
      const url = `/events/sessions/${sessionId}?from=${lastEventIdRef.current}`;
      source = new EventSource(url, { withCredentials: true });

      source.onopen = () => {
        retryRef.current = 0;
        dispatch({ type: "connection", status: "open" });
      };

      source.addEventListener(SSE_ACTIVITY_EVENT, (event) => {
        let activity: Activity;
        try {
          activity = JSON.parse((event as MessageEvent).data) as Activity;
        } catch {
          // A malformed frame is not worth tearing the view down over.
          return;
        }
        dispatch({ type: "activity", activity });

        // The terminal activity carries the site URL and nothing else, so a
        // viewer who watched the build live never learns which Xano tenant is
        // behind it — their snapshot was fetched minutes earlier, before the
        // deploy existed. One refetch at the end closes that gap.
        //
        // Widening the terminal activity instead would put the tenant in the
        // durable event log and in every replay of it, to be read once at the
        // very end of one build. The snapshot is already the authority on what
        // a finished session is; this asks it.
        if (activity.kind === "terminal" && activity.outcome === "succeeded") {
          void seedFromSnapshot();
        }
      });

      source.onerror = () => {
        source?.close();
        if (cancelled) return;

        dispatch({ type: "connection", status: "reconnecting" });
        // Capped exponential backoff. Quiet by design — see the module comment.
        const delay = Math.min(1000 * 2 ** retryRef.current, 10_000);
        retryRef.current += 1;
        retryTimer = setTimeout(connect, delay);
      };
    };

    connect();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      source?.close();
    };
  }, [sessionId]);

  const isTerminal = state.outcome !== null;

  // Steps, the keyed file map, and the canvas state. Derived rather than
  // accumulated so it stays a pure function of the activity list — replay and
  // live append produce identical output.
  const view = useMemo(() => buildViewModel(state.activities), [state.activities]);

  return { ...state, isTerminal, ...view };
}

/** Create a session and submit its prompt. Returns the session id. */
export async function startBuild(
  prompt: string,
  framework: Framework = DEFAULT_FRAMEWORK,
  deepThink: boolean = DEFAULT_DEEP_THINK,
): Promise<{ sessionId: string } | { error: string }> {
  // Both sent on creation, not with the prompt: the relay picks a baked
  // template and composes the instructions before the agent's conversation
  // exists, so this is the last point either choice is still free. The API
  // defaults both anyway, so a caller that omits the arguments behaves exactly
  // as it did before the controls existed.
  const created = await fetch("/api/sessions", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ framework, deepThink }),
  });
  if (!created.ok) return { error: "Could not start a session. Please try again." };
  const { sessionId } = (await created.json()) as { sessionId: string };

  const submitted = await fetch(`/api/sessions/${sessionId}/prompt`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ prompt }),
  });

  if (!submitted.ok) {
    const body = (await submitted.json().catch(() => ({}))) as { message?: string };
    return { error: body.message ?? "Could not start the build." };
  }
  return { sessionId };
}

export const useStreamReducerForTest = { reduce, INITIAL };
